import { Money } from "../../model/common/money";
import { UserResponse } from "./user";

export enum PaymentPlan {
	Undefined = 0,
	Installment = 1,
	OneTime = 2,
	PayAsYouGo = 3,
}

export enum RecommendationRequestStatus {
	Undefined = 0,
  Initial = 1,
  FinalStatus = 2,
  ChosingProvider = 3,
  CheckingDigitalLoadAssessment = 4,
  CarringOutPhysicalLoadAssessment = 5,
  SendingProductsThatFit = 6,
  Pending = 7,
  Passive = 8,
  NoProvider = 9,
}

export enum PurchaseTimelineType {
	Undefined = 0,
	ReadyToBuyNow = 1,
	ReadyToBuyInTheNextThreeMonths = 2,
	NeedQuotationForAleterDate = 3,
}

export enum CompanyStatus {
	Pending = 1,
	Active = 2,
	Suspended = 3,
}

export enum EventType {
	SolarRecommendation = 1,
	CreditCheck = 2,
	Order = 3,
	OrderInstallment = 4,
}

export interface RecommendationAppliance {
	id: string;
	applianceId: number;
	quantity: number;
	usage: number;
	name: string;
}

export interface PhysicalLoadAssessmentResponse {
	scheduledDate: string;
	appliances: RecommendationAppliance[];
	kva: number;
	totalWatts: number;
	kwh: number;
}

export interface DigitalLoadAssessmentResponse {
	appliances: RecommendationAppliance[];
	kva: number;
	totalWatts: number;
	kwh: number;
}

export interface InstallmentPlanDetailsResponse {
	solarMonthlyRepayment: Money;
	paymentPeriod: number;
}

export interface OneTimePlanDetailsResponse {
	oneTimePaymentAmount: Money;
}

export interface PaymentPlanDetailsResponse {
	alternatePowerExpense: Money;
	installmentPlanDetails?: InstallmentPlanDetailsResponse;
	oneTimePlanDetails?: OneTimePlanDetailsResponse;
	timelineType: PurchaseTimelineType;
}

export interface InstallationDetailsResponse {
	authorizedToInstallOnRoof: boolean;
	numberOfHoursOfElectricityPerDay: number;
	address: string;
}

export interface RecommendationRequestStatusDetailsResponse {
	status: RecommendationRequestStatus;
	updatedAt: string;
}

export interface SolarProductImageResponse {
	id: string;
	imageUrl: string;
}

export interface CategoryResponse {
	id: string;
	name: string;
	imageUrl: string;
}

export interface MonthlyPaymentOption {
	deposit: Money;
	totalPrice: Money;
	durationInMonths: number;
	monthlyPayment: Money;
}

export interface StoreProductPriceBreakdown {
	price: Money;
	monthlyPaymentOptions: MonthlyPaymentOption[];
}

export interface PayAsYouGoDetailsResult {
	costPerKwh: Money;
}

export interface CompanyResponse {
	id: string;
	name: string;
	businessInfo?: string;
	businessType: string;
	rcNumber: string;
	address: string;
	state: string;
	city: string;
	createdAt: string;
	companyStatus: CompanyStatus;
	ownerId: string;
}

export interface StoreProductDetailsResponse {
	id: string;
	productName: string;
	productCapacity: number;
	activeDuration?: number;
	packageDescription?: string;
	batteryType?: string;
	inverterType?: string;
	warranty?: string;
	batteryVoltage?: string;
	installationCostForBungalow: Money;
	installationCostForFloorForStoryBuilding: Money;
	hotDeal: boolean;
	type: string;
	quantityAvailableForSale: number;
	numberOfViews: number;
	createdAt: string;
	images: SolarProductImageResponse[];
	company?: CompanyResponse;
	categories: CategoryResponse[];
	priceBreakdown: StoreProductPriceBreakdown;
	payAsYouGoDetails?: PayAsYouGoDetailsResult;
}

export interface AssessmentDetailsResponse {
	id: string;
	userId: string;
	paymentPlan: PaymentPlan;
	paymentPlanDetails?: PaymentPlanDetailsResponse;
	installationDetails: InstallationDetailsResponse;
	physicalLoadAssessment?: PhysicalLoadAssessmentResponse;
	digitalLoadAssessment: DigitalLoadAssessmentResponse;
	recommendedProducts: StoreProductDetailsResponse[];
	selectedProviderId?: string;
	selectedProductId?: string;
	solarProduct?: StoreProductDetailsResponse;
	country: string;
	statusDetails: RecommendationRequestStatusDetailsResponse;
	updatedAt: string;
}

export interface EventHistoryDetails {
	workflowId: string;
	timestamp: string;
	typeOfEvent: number;
	executedBy?: string;
	type: EventType;
	executerName?: string;
	eventTitle: string;
	eventDescription: string;
}

const solarRecommendationEventTitles: Record<number, string> = {
	1: "Created Digital Load Assessment",
	2: "No Provider For Customer",
	3: "Your Order Has Been Assigned To A Solar Provider",
	4: "Checked Digital Load Assessment",
	5: "Carried Out Physical Load Assessment",
	6: "No Products For Customer",
	7: "Send Products For Customer",
	8: "Selected Product",
	9: "Timeout",
	10: "Satisfied With Recommendation",
	11: "Rejected Recommendation",
	12: "Schedule Physical Load Assessment",
	13: "Reschedule Physical Load Assessment",
	14: "Overdue Physical Load Assessment",
	15: "Start Physical Load Assessment",
};

const solarRecommendationEventDescriptions: Record<number, string> = {
	1: "Your assessment has been created",
	2: "No provider available",
	3: "They will contact you shortly to proceed with the next steps",
	4: "Digital assessment reviewed",
	5: "Physical load assessment performed",
	6: "No products available",
	7: "Dispatch products to customer",
	8: "Product selected",
	9: "Operation timed out",
	10: "Customer satisfied with recommendation",
	11: "Customer rejected recommendation",
	12: "Assessment Scheduled",
	13: "Assessment Rescheduled",
	14: "Assessment Overdue",
	15: "Assessment Started",
};

export const getEventTitle = (type: EventType, typeOfEvent: number): string => {
	if (type === EventType.SolarRecommendation) {
		return solarRecommendationEventTitles[typeOfEvent] ?? "Unknown Event";
	}
	return "Unknown Event";
};

export const getSolarRecommendationEventDescription = (type: EventType, typeOfEvent: number): string => {
	if (type === EventType.SolarRecommendation) {
		return solarRecommendationEventDescriptions[typeOfEvent] ?? "";
	}
	return "";
};

export interface RecommendationDetails {
	id: string;
	createdAt: string;
	status: number;
	assessmentDetails?: AssessmentDetailsResponse;
	user?: UserResponse;
	company?: CompanyResponse;
	events: EventHistoryDetails[];
}
